import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AWNLogo from '../components/AWNLogo';
import useAuthEmailCooldown from '../hooks/useAuthEmailCooldown';
import {
  AUTH_OUTCOMES,
  logUserEvent,
  normalizeEmail,
  registerUser,
  resendVerificationEmail,
  signInWithGoogle,
} from '../services/authService';
import { getPasswordStrength, isValidEmail, validateNewPassword } from '../utils/authValidation';
import styles from '../style/RegistrationPage.module.css';

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  agreeTerms: false,
};

function GoogleIcon() {
  return (
    <svg className={styles.socialIcon} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function RegistrationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialEmail = typeof location.state?.email === 'string' ? location.state.email : '';
  const [formData, setFormData] = useState({ ...EMPTY_FORM, email: initialEmail });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [stage, setStage] = useState('form');
  const [busyAction, setBusyAction] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { secondsRemaining, startCooldown } = useAuthEmailCooldown();

  const normalizedEmail = normalizeEmail(formData.email);
  const passwordStrength = useMemo(
    () => getPasswordStrength(formData.password),
    [formData.password]
  );
  const strengthLabels = ['Sehr schwach', 'Schwach', 'Mittel', 'Gut', 'Stark', 'Sehr stark'];
  const strengthColors = ['#dc3545', '#dc3545', '#fd7e14', '#d49a00', '#058744', '#04733a'];

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setStatus(null);
  };

  const validateForm = () => {
    const nextErrors = validateNewPassword(formData.password, formData.confirmPassword);
    if (!formData.firstName.trim()) nextErrors.firstName = 'Vorname ist erforderlich.';
    if (!formData.lastName.trim()) nextErrors.lastName = 'Nachname ist erforderlich.';
    if (!isValidEmail(formData.email)) nextErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    if (!formData.agreeTerms) nextErrors.agreeTerms = 'Bitte stimmen Sie den Bedingungen zu.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const showExistingAccount = () => {
    setFormData((current) => ({ ...current, password: '', confirmPassword: '' }));
    setStage('existing');
    setStatus(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(null);
    if (!validateForm()) return;

    setBusyAction('register');
    try {
      const result = await registerUser(normalizedEmail, formData.password, {
        full_name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
      });

      if (result.outcome === AUTH_OUTCOMES.EXISTING_ACCOUNT) {
        showExistingAccount();
        return;
      }

      if (result.outcome === AUTH_OUTCOMES.SIGNED_IN) {
        await logUserEvent(result.user?.id, 'signup');
        navigate('/dashboard', { replace: true });
        return;
      }

      setFormData((current) => ({ ...current, email: normalizedEmail, password: '', confirmPassword: '' }));
      setStage('confirmation');
      startCooldown();
    } catch (error) {
      if (error?.code === 'already_registered') {
        showExistingAccount();
      } else {
        setStatus({ type: 'error', message: error.message });
      }
    } finally {
      setBusyAction(null);
    }
  };

  const handleResendVerification = async () => {
    if (!normalizedEmail || secondsRemaining > 0) return;
    setBusyAction('resend');
    setStatus(null);
    try {
      await resendVerificationEmail(normalizedEmail);
      setStatus({
        type: 'success',
        message: 'Die Bestätigungs-E-Mail wurde erneut angefordert. Prüfen Sie bitte auch Spam und Werbung.',
      });
      startCooldown();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
      if (error?.code?.includes('rate_limit')) startCooldown();
    } finally {
      setBusyAction(null);
    }
  };

  const handleGoogleSignUp = async () => {
    setBusyAction('google');
    setStatus(null);
    try {
      await signInWithGoogle();
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Die Google-Anmeldung konnte nicht gestartet werden.' });
      setBusyAction(null);
    }
  };

  const returnToForm = () => {
    setStage('form');
    setStatus(null);
    setErrors({});
  };

  const goToLogin = () => navigate('/login', { state: { email: normalizedEmail, notice: 'existing_account' } });
  const goToPasswordReset = () => navigate('/forgot-password', { state: { email: normalizedEmail } });

  return (
    <div className={styles.pageContainer}>
      <div className={styles.backgroundPattern} aria-hidden="true" />
      <div className={styles.splitContainer}>
        <aside className={styles.socialColumn}>
          <div className={styles.socialLogoSection}>
            <AWNLogo width={360} height={120} className={styles.companyLogo} />
          </div>
          <p className={styles.socialHint}>Am schnellsten geht es mit Ihrem Google-Konto.</p>
          <div className={styles.socialButtonsStacked}>
            <button type="button" className={styles.googleButton} onClick={handleGoogleSignUp} disabled={Boolean(busyAction)}>
              <GoogleIcon />
              <span>{busyAction === 'google' ? 'Google wird geöffnet…' : 'Mit Google fortfahren'}</span>
            </button>
          </div>
        </aside>

        <section className={styles.signupCard}>
          {stage === 'form' && (
            <>
              <div className={styles.logoSection}>
                <p className={styles.eyebrow}>In wenigen Schritten</p>
                <h1 className={styles.signupTitle}>AWN-Konto erstellen</h1>
                <p className={styles.signupSubtitle}>Ein Konto, alle AWN AI Werkzeuge.</p>
              </div>

              {status && (
                <div className={status.type === 'error' ? styles.errorText : styles.successText} role="alert">{status.message}</div>
              )}

              <form onSubmit={handleSubmit} className={styles.signupForm} noValidate>
                <div className={styles.nameRow}>
                  <div className={styles.inputGroup}>
                    <label htmlFor="firstName">Vorname</label>
                    <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} autoComplete="given-name" className={errors.firstName ? styles.errorInput : ''} aria-invalid={Boolean(errors.firstName)} disabled={Boolean(busyAction)} />
                    {errors.firstName && <span className={styles.fieldError}>{errors.firstName}</span>}
                  </div>
                  <div className={styles.inputGroup}>
                    <label htmlFor="lastName">Nachname</label>
                    <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} autoComplete="family-name" className={errors.lastName ? styles.errorInput : ''} aria-invalid={Boolean(errors.lastName)} disabled={Boolean(busyAction)} />
                    {errors.lastName && <span className={styles.fieldError}>{errors.lastName}</span>}
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="email">E-Mail-Adresse</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} autoComplete="email" inputMode="email" className={errors.email ? styles.errorInput : ''} aria-invalid={Boolean(errors.email)} disabled={Boolean(busyAction)} />
                  {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="password">Passwort</label>
                  <div className={styles.inputWrapper}>
                    <input type={showPassword ? 'text' : 'password'} id="password" name="password" value={formData.password} onChange={handleInputChange} autoComplete="new-password" className={errors.password ? styles.errorInput : ''} aria-invalid={Boolean(errors.password)} aria-describedby="password-help" disabled={Boolean(busyAction)} />
                    <button type="button" className={styles.passwordToggleIcon} onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'}>
                      {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                    </button>
                  </div>
                  <div id="password-help" className={styles.passwordHelp}>Mindestens 8 Zeichen; 12 oder mehr sind besser.</div>
                  {formData.password && (
                    <div className={styles.passwordStrength} aria-label={`Passwortstärke: ${strengthLabels[passwordStrength]}`}>
                      <div className={styles.strengthBar}>
                        <div className={styles.strengthFill} style={{ width: `${Math.max(10, passwordStrength * 20)}%`, backgroundColor: strengthColors[passwordStrength] }} />
                      </div>
                      <span className={styles.strengthText} style={{ color: strengthColors[passwordStrength] }}>{strengthLabels[passwordStrength]}</span>
                    </div>
                  )}
                  {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="confirmPassword">Passwort wiederholen</label>
                  <div className={styles.inputWrapper}>
                    <input type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} autoComplete="new-password" className={errors.confirmPassword ? styles.errorInput : ''} aria-invalid={Boolean(errors.confirmPassword)} disabled={Boolean(busyAction)} />
                    <button type="button" className={styles.passwordToggleIcon} onClick={() => setShowConfirmPassword((visible) => !visible)} aria-label={showConfirmPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'}>
                      {showConfirmPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className={styles.fieldError}>{errors.confirmPassword}</span>}
                </div>

                <div className={styles.termsGroup}>
                  <label className={styles.termsLabel}>
                    <input type="checkbox" name="agreeTerms" checked={formData.agreeTerms} onChange={handleInputChange} className={styles.termsCheckbox} disabled={Boolean(busyAction)} />
                    <span className={styles.checkmark} aria-hidden="true" />
                    <span>Ich stimme den <Link to="/nutzungsbedingungen" className={styles.termsLink}>Nutzungsbedingungen</Link> und der <Link to="/datenschutz" className={styles.termsLink}>Datenschutzerklärung</Link> zu.</span>
                  </label>
                  {errors.agreeTerms && <span className={styles.fieldError}>{errors.agreeTerms}</span>}
                </div>

                <button type="submit" className={styles.submitButton} disabled={Boolean(busyAction)}>
                  {busyAction === 'register' ? 'Konto wird erstellt…' : 'Konto erstellen'}
                </button>

                <div className={styles.loginSection}>
                  <p>Schon registriert?</p>
                  <Link to="/login" state={{ email: normalizedEmail }} className={styles.loginLink}>Jetzt anmelden</Link>
                </div>
              </form>
            </>
          )}

          {stage === 'existing' && (
            <div className={styles.outcomePanel} aria-live="polite">
              <div className={styles.outcomeIcon} aria-hidden="true">✓</div>
              <p className={styles.eyebrow}>Konto bereits vorhanden</p>
              <h1 className={styles.signupTitle}>Diese E-Mail ist schon registriert</h1>
              <p className={styles.outcomeCopy}>Für <strong>{normalizedEmail}</strong> kann kein neues Konto erstellt werden. Melden Sie sich direkt an oder setzen Sie Ihr Passwort zurück.</p>
              <button type="button" className={styles.submitButton} onClick={goToLogin}>Jetzt anmelden</button>
              <button type="button" className={styles.secondaryButton} onClick={goToPasswordReset}>Passwort vergessen?</button>
              <button type="button" className={styles.textButton} onClick={returnToForm}>Andere E-Mail verwenden</button>
            </div>
          )}

          {stage === 'confirmation' && (
            <div className={styles.outcomePanel} aria-live="polite">
              <div className={styles.outcomeIcon} aria-hidden="true">✉</div>
              <p className={styles.eyebrow}>Fast geschafft</p>
              <h1 className={styles.signupTitle}>E-Mail bestätigen</h1>
              <p className={styles.outcomeCopy}>Wir haben eine Bestätigungs-E-Mail für <strong>{normalizedEmail}</strong> angefordert.</p>
              <ol className={styles.checklist}>
                <li>Posteingang sowie Spam- und Werbung-Ordner prüfen.</li>
                <li>Auf „E-Mail bestätigen“ klicken.</li>
                <li>Danach mit E-Mail und Passwort anmelden.</li>
              </ol>
              {status && <div className={status.type === 'error' ? styles.errorText : styles.successText} role="status">{status.message}</div>}
              <button type="button" className={styles.submitButton} onClick={goToLogin}>Zur Anmeldung</button>
              <button type="button" className={styles.secondaryButton} onClick={handleResendVerification} disabled={busyAction === 'resend' || secondsRemaining > 0}>
                {busyAction === 'resend' ? 'E-Mail wird angefordert…' : secondsRemaining > 0 ? `Erneut senden in ${secondsRemaining}s` : 'Bestätigungs-E-Mail erneut senden'}
              </button>
              <button type="button" className={styles.textButton} onClick={returnToForm}>E-Mail-Adresse korrigieren</button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default RegistrationPage;
